import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest, map } from 'rxjs';
import * as Cesium from 'cesium';
import {
  VehicleCampusFence,
  VehicleCampusGroup,
  VehicleMonitorService
} from '../../../../core/services/vehicle-monitor.service';
import { CoresService } from '../../../../core/services/cores.service';

interface CampusRow {
  id: string;
  label: string;
  authorisedInside: number;
  authorisedOutside: number;
  unauthorisedInside: number;
  unauthorisedOutside: number;
  total: number;
  geometry?: VehicleCampusFence['geometry'];
}

@Component({
  selector: 'app-vehicle-campus-legend',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './vehicle-campus-legend.component.html',
  styleUrl: './vehicle-campus-legend.component.css',
})
export class VehicleCampusLegendComponent {
  private readonly vehicleMonitor = inject(VehicleMonitorService);
  private readonly cores = inject(CoresService);
  isExpanded = true;

  readonly view$ = combineLatest([
    this.vehicleMonitor.latestSnapshot$,
    this.vehicleMonitor.selectedCampusId$
  ]).pipe(
    map(([snapshot, selectedId]) => {
      const groups = snapshot?.counts?.byCampus || [];
      const fences = snapshot?.campuses || [];
      const rows: CampusRow[] = (fences.length ? fences : groups).map((campus: VehicleCampusFence | VehicleCampusGroup) => {
        const group = groups.find((row) => row.id === campus.id || row.label === campus.label);
        return {
          id: campus.id,
          label: campus.label,
          authorisedInside: group?.authorisedInside || 0,
          authorisedOutside: group?.authorisedOutside || 0,
          unauthorisedInside: group?.unauthorisedInside || 0,
          unauthorisedOutside: group?.unauthorisedOutside || 0,
          total: group?.total || 0,
          geometry: 'geometry' in campus ? campus.geometry : undefined
        };
      });
      return {
        rows,
        selectedId,
        authorised: snapshot?.counts?.authorised || 0,
        unauthorised: snapshot?.counts?.unauthorised || 0,
        total: snapshot?.counts?.total || 0
      };
    })
  );

  toggleCollapse(event: Event): void {
    event.stopPropagation();
    this.isExpanded = !this.isExpanded;
  }

  onCampusClick(row: CampusRow, event: Event): void {
    event.stopPropagation();
    const next = this.vehicleMonitor.selectedCampusId() === row.id ? null : row.id;
    this.vehicleMonitor.selectCampus(next);
    if (next && row.geometry) {
      this.flyToGeometry(row.geometry);
    }
  }

  onOverviewClick(event: Event): void {
    event.stopPropagation();
    this.vehicleMonitor.selectCampus(null);
  }

  private flyToGeometry(geometry: { type?: string; coordinates?: unknown }): void {
    const viewer = this.cores.getViewer() as Cesium.Viewer | undefined;
    if (!viewer?.camera || !geometry?.coordinates) {
      return;
    }
    const lons: number[] = [];
    const lats: number[] = [];
    const walk = (node: unknown): void => {
      if (!Array.isArray(node) || !node.length) {
        return;
      }
      if (typeof node[0] === 'number' && typeof node[1] === 'number') {
        lons.push(node[0]);
        lats.push(node[1]);
        return;
      }
      node.forEach(walk);
    };
    walk(geometry.coordinates);
    if (!lons.length) {
      return;
    }
    const rectangle = Cesium.Rectangle.fromDegrees(
      Math.min(...lons),
      Math.min(...lats),
      Math.max(...lons),
      Math.max(...lats)
    );
    viewer.camera.flyTo({ destination: rectangle, duration: 1.4 });
  }
}
